import type { FC } from "react";
import { Checkbox, Column, FormField, Select } from "@hightouch/htui";
import { DISCLAIMER_POSITIONS } from "@hightouch/lib/ad-studio/disclaimer/constants";
import type {
  DisclaimerPosition,
  DisclaimerSpec,
} from "@hightouch/lib/ad-studio/disclaimer/types";

const POSITION_LABELS: Record<DisclaimerPosition, string> = {
  "bottom-left": "Bottom left",
  "bottom-right": "Bottom right",
  "top-left": "Top left",
  "top-right": "Top right",
};

const POSITION_OPTIONS = DISCLAIMER_POSITIONS.map((value) => ({
  value,
  label: POSITION_LABELS[value],
}));

export interface DisclaimerControlsProps {
  /** Checkbox state; `mixed` renders the indeterminate state. */
  visible: boolean;
  visibleMixed: boolean;
  machineReadable: boolean;
  machineReadableMixed: boolean;
  /** Selected position, or `null` when the selection is mixed. */
  position: DisclaimerPosition | null;
  positionMixed: boolean;
  onVisibleChange: (value: boolean) => void;
  onMachineReadableChange: (value: boolean) => void;
  onPositionChange: (value: DisclaimerPosition) => void;
}

export const DisclaimerControls: FC<DisclaimerControlsProps> = ({
  visible,
  visibleMixed,
  machineReadable,
  machineReadableMixed,
  position,
  positionMixed,
  onVisibleChange,
  onMachineReadableChange,
  onPositionChange,
}) => {
  // A mixed selection reads as "not all on", so toggling resolves everything on.
  const nextVisible = visibleMixed ? true : !visible;
  const nextMachineReadable = machineReadableMixed ? true : !machineReadable;

  return (
    <Column gap={4}>
      <Checkbox
        label="Visible disclosure"
        description='Overlays "Contains AI-generated content" onto the exported image.'
        isChecked={visible}
        isIndeterminate={visibleMixed}
        onChange={() => onVisibleChange(nextVisible)}
      />
      <FormField label="Position">
        <Select
          isDisabled={!visible && !visibleMixed}
          placeholder={positionMixed ? "More than one setting" : undefined}
          options={POSITION_OPTIONS}
          value={position ?? undefined}
          onChange={(value) => {
            if (value) onPositionChange(value);
          }}
        />
      </FormField>
      <Checkbox
        label="Machine-readable disclosure"
        description="Embeds an IPTC “AI generated” tag in the file's metadata."
        isChecked={machineReadable}
        isIndeterminate={machineReadableMixed}
        onChange={() => onMachineReadableChange(nextMachineReadable)}
      />
    </Column>
  );
};

export interface SingleAdDisclaimerControlsProps {
  value: DisclaimerSpec;
  onChange: (value: DisclaimerSpec) => void;
}

/**
 * Single-ad convenience wrapper around `DisclaimerControls` for callers that
 * edit one `DisclaimerSpec` (launch, video export) and never have a mixed
 * selection.
 */
export const SingleAdDisclaimerControls: FC<
  SingleAdDisclaimerControlsProps
> = ({ value, onChange }) => (
  <DisclaimerControls
    visible={value.visible}
    visibleMixed={false}
    machineReadable={value.machineReadable}
    machineReadableMixed={false}
    position={value.position}
    positionMixed={false}
    onVisibleChange={(visible) => onChange({ ...value, visible })}
    onMachineReadableChange={(machineReadable) =>
      onChange({ ...value, machineReadable })
    }
    onPositionChange={(position) => onChange({ ...value, position })}
  />
);
